import { h } from "preact";
import { useState, useRef } from "preact/hooks";
import useMonogram from "../../helpers/useMonogram/useMonogram";
import { ArrowUpRight, Check, MonogramMark } from "../../helpers/icons";
import { formatMonogramPrice } from "../monogram/monogramTypeConfig";
import {
  loadMonogramDefault,
  saveMonogramDefault,
} from "../monogram/monogramDefaults";
import MonogramTriggerContent from "./monogram-trigger-content";
import Monogram from "../monogram/monogram";

// `small` selects the compact, inline trigger style used inside product
// add-ons; left unset it renders the default boxed style.
export default function MonogramTrigger({
  handle,
  value,
  onChange,
  currentVariant,
  small = false,
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const added = Boolean(value);
  const addRef = useRef();
  const editRef = useRef();
  // The last configured monogram is kept so the summary and price fade out with
  // the collapsing panel rather than blanking the instant it is removed.
  const lastValue = useRef(null);
  if (value) lastValue.current = value;
  const displayValue = value || lastValue.current;

  // Fetch the monogram component once the modal has been opened (or kept around
  // while a monogram is configured, so editing reopens without a refetch).
  const [component] = useMonogram(modalOpen || added ? handle : null);
  if (!handle) return null;

  const price = formatMonogramPrice(displayValue?.variant);

  return (
    <div
      className={`monogram-trigger ${small ? "monogram-trigger--small" : ""} ${
        added ? "monogram-trigger--added" : ""
      }`}
    >
      <div className="monogram-trigger__inner">
        {!added && (
          <button
            type="button"
            className="monogram-trigger__expand"
            aria-expanded={modalOpen}
            aria-haspopup="dialog"
            aria-label={window.localeStrings.product.monogram_add}
            onClick={() => setModalOpen(true)}
            ref={addRef}
          />
        )}
        <span className="monogram-trigger__image-wrapper">
          <span className="monogram-trigger__image">
            <MonogramMark />
          </span>
        </span>
        <div className="monogram-trigger__header">
          <span className="monogram-trigger__title">
            {added
              ? window.localeStrings.monogram.monogram
              : window.localeStrings.product.monogram_add}
          </span>
          {small && (
            <span className="monogram-trigger__check">
              <Check />
            </span>
          )}
          <span className="monogram-trigger__aside">
            <span className="monogram-trigger__icon">
              <ArrowUpRight />
            </span>
            <span className="monogram-trigger__price">
              {price && `+${price}`}
            </span>
          </span>
        </div>
        <MonogramTriggerContent
          added={added}
          value={displayValue?.data}
          editRef={editRef}
          onEdit={() => setModalOpen(true)}
          onRemove={() => onChange(null)}
        />
      </div>
      <Monogram
        open={modalOpen}
        returnFocusRef={added ? editRef : addRef}
        monogram={component}
        initialSelection={value?.selection || loadMonogramDefault()}
        currentVariant={currentVariant}
        onClose={() => setModalOpen(false)}
        onAdd={(monogram) => {
          saveMonogramDefault(monogram.selection);
          onChange(monogram);
          setModalOpen(false);
        }}
      />
    </div>
  );
}
